import { useCallback, useRef, useState } from 'react';
import Image from 'next/image';
import gsap from 'gsap/dist/gsap';

import style from './CompareBlock.module.scss';
import { ImageBlock, BlockImageProps } from '../imageBlock/ImageBlock';
import { DefaultBlockProps } from '../switchBlock/SwitchBlock';
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect';

export type BlockCompareType = {
  block_type: 'compare';
  compare_items: DefaultBlockProps[];
};

export const CompareBlock = ({ compare_items }: BlockCompareType): JSX.Element => {
  const [maskRefLoaded, setMaskRefLoaded] = useState(false);
  const maskRef = useRef<HTMLDivElement | null>(null);
  const setMaskRef = useCallback((node: HTMLDivElement) => {
    maskRef.current = node;
    if (node) setMaskRefLoaded(true);
  }, []);

  const [handleRefLoaded, setHandleRefLoaded] = useState(false);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const setHandleRef = useCallback((node: HTMLDivElement) => {
    handleRef.current = node;
    if (node) setHandleRefLoaded(true);
  }, []);

  const [rangeRefLoaded, setRangeRefLoaded] = useState(false);
  const rangeRef = useRef<HTMLInputElement | null>(null);
  const setRangeRef = useCallback((node: HTMLInputElement) => {
    rangeRef.current = node;
    if (node) setRangeRefLoaded(true);
  }, []);

  useIsomorphicLayoutEffect(() => {
    let ignore = false;

    const animate = () =>
      window.requestAnimationFrame(() => {
        if (rangeRef.current && maskRef.current && handleRef.current) {
          gsap.to(maskRef.current, { width: `${Number(rangeRef.current.value) * 100}%` });
          gsap.to(handleRef.current, { left: `${Number(rangeRef.current.value) * 100}%` });
        }
        !ignore && animate();
      });
    if (rangeRef.current && maskRef.current && handleRef.current) {
      animate();
    }
    return () => {
      ignore = true;
    };
  }, [rangeRefLoaded, maskRefLoaded, handleRefLoaded]);

  return (
    <div className={style.map}>
      <div
        className={style.mapContainer}
        style={{
          aspectRatio: `${compare_items[0].image_width} / ${compare_items[0].image_height}`,
        }}
      >
        {compare_items.map((item, index: number) => {
          const desktop: BlockImageProps = {
            src: item.image,
            objectFit: 'cover',
            layout: 'fill',
          };
          const mobile: BlockImageProps | undefined = item.phone_image
            ? {
                src: item.phone_image,
                objectFit: 'cover',
                layout: 'fill',
              }
            : undefined;
          return (
            <div key={index} className={style.mapImage}>
              {index == 1 ? (
                <div className={style.mask} ref={setMaskRef}>
                  <ImageBlock desktop={desktop} mobile={mobile} alt={item.alt_text} />
                </div>
              ) : (
                <ImageBlock desktop={desktop} mobile={mobile} alt={item.alt_text} />
              )}
            </div>
          );
        })}

        <div className={style.slider}>
          <div className={style.sliderHandle} ref={setHandleRef}>
            <div className={style.sliderHandleBorder}></div>
            <div className={style.sliderHandleIcon}>
              <span className={style.sliderHandleInner}>
                <Image
                  src="/slider-handle.svg"
                  alt="slider"
                  width={36}
                  height={23}
                  unoptimized={true}
                  className={style.sliderHandleImage}
                />
              </span>
            </div>
          </div>
          <input
            ref={setRangeRef}
            type="range"
            min="0"
            max="1"
            step="0.01"
            defaultValue={0.5}
          />
        </div>
      </div>
      <div className={style.footer}>
        {compare_items.map((item, index: number) => (
          <div key={`alt-${index}`}>{item.alt_text}</div>
        ))}
      </div>
    </div>
  );
};
